// ── Sky-quality derivations ─────────────────────────────────────────────────
// The verdict endpoint returns a Bortle class (estimated from VIIRS radiance).
// The Tonight "Location Details" card wants the human-facing numbers that ride
// on top of Bortle: sky-glow brightness (SQM), naked-eye limiting magnitude
// (NELM), an approximate visible-star count, and Milky Way visibility. These
// are STANDARD approximate mappings of the Bortle scale (Crumey / classic
// Bortle ranges), not independent measurements, so we derive them on the
// client rather than round-tripping the API. Each class returns a single
// representative value; callers should treat them as estimates.

const SKY = {
  1: { sqm: 21.9, nelm: 7.8, stars: 3500, milkyway: 'Casts shadows; zodiacal light obvious',         mwValence: 'good' },
  2: { sqm: 21.7, nelm: 7.3, stars: 2500, milkyway: 'Highly structured; summer band easy',           mwValence: 'good' },
  3: { sqm: 21.4, nelm: 6.8, stars: 2000, milkyway: 'Rich detail; dark rifts visible',               mwValence: 'good' },
  4: { sqm: 21.0, nelm: 6.3, stars: 1500, milkyway: 'Visible; structure near the zenith',            mwValence: 'good' },
  5: { sqm: 20.5, nelm: 5.8, stars: 1000, milkyway: 'Washed out; only the brightest band overhead',  mwValence: 'mid'  },
  6: { sqm: 19.6, nelm: 5.3, stars: 500,  milkyway: 'Only a faint hint at the zenith',               mwValence: 'mid'  },
  7: { sqm: 18.8, nelm: 4.8, stars: 250,  milkyway: 'Effectively invisible',                         mwValence: 'bad'  },
  8: { sqm: 18.2, nelm: 4.3, stars: 100,  milkyway: 'Invisible; sky glows grey-orange',              mwValence: 'bad'  },
  9: { sqm: 17.8, nelm: 4.0, stars: 50,   milkyway: 'Invisible; only the brightest stars survive',   mwValence: 'bad'  },
};

// Bortle → derived sky quality. Returns null when class is unknown / out of
// coverage so callers can render an honest "no estimate" state.
export function bortleToSky(bortle) {
  if (bortle == null || !SKY[bortle]) return null;
  return SKY[bortle];
}

// Format an approximate star count as a friendly range, e.g. 1500 -> "1,500–2,000".
export function starRange(stars) {
  if (stars == null) return null;
  const hi = Math.round(stars * 1.33 / 100) * 100;
  return `${stars.toLocaleString()}\u2013${hi.toLocaleString()}`;
}

// Position (0..1) of a Bortle class along the B1(dark)→B9(bright) gradient bar.
export function bortleBarPct(bortle) {
  if (bortle == null) return 0;
  return Math.min(1, Math.max(0, (bortle - 1) / 8));
}

// Overall skyglow brightness (0 = pristine, 1 = inner city) for tinting the
// skyglow visualization. Derived from Bortle, nudged brighter by an up Moon.
export function skyGlowLevel(bortle, moonUp, moonPhaseFraction) {
  const base = bortle == null ? 0.45 : Math.min(1, Math.max(0, (bortle - 1) / 8));
  const moon = moonUp ? 0.35 * (moonPhaseFraction ?? 0) : 0;
  return Math.min(1, base + moon * (1 - base));
}
